import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  ChoiceList,
  Checkbox,
  Spinner, Banner, List
} from '@shopify/polaris';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import { useTranslation } from 'react-i18next';
import useAxiosClient from '../axiosClient';

export default function EditStaff() {
  const { t } = useTranslation();
  const appBridge = useAppBridge();
  const { id } = useParams();
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const CURRENCIES = [
    { label: 'US Dollar (USD)', value: 'USD' },
    { label: 'Euro (EUR)', value: 'EUR' },
    { label: 'British Pound (GBP)', value: 'GBP' },
    { label: 'Japanese Yen (JPY)', value: 'JPY' },
    { label: 'Australian Dollar (AUD)', value: 'AUD' },
    { label: 'Canadian Dollar (CAD)', value: 'CAD' },
    { label: 'Swiss Franc (CHF)', value: 'CHF' },
    { label: 'Chinese Yuan (CNY)', value: 'CNY' },
    { label: 'Indian Rupee (INR)', value: 'INR' },
    { label: 'Singapore Dollar (SGD)', value: 'SGD' },
    { label: 'New Zealand Dollar (NZD)', value: 'NZD' },
    { label: 'Hong Kong Dollar (HKD)', value: 'HKD' },
    { label: 'Swedish Krona (SEK)', value: 'SEK' },
    { label: 'South Korean Won (KRW)', value: 'KRW' },
    { label: 'Brazilian Real (BRL)', value: 'BRL' },
    { label: 'Russian Ruble (RUB)', value: 'RUB' },
    { label: 'South African Rand (ZAR)', value: 'ZAR' },
    { label: 'Mexican Peso (MXN)', value: 'MXN' },
    { label: 'United Arab Emirates Dirham (AED)', value: 'AED' },
    { label: 'Danish Krone (DKK)', value: 'DKK' }
  ];
  const DEFAULT_AVAILABILITY = {
    monday: { available: true },
    tuesday: { available: true },
    wednesday: { available: true },
    thursday: { available: true },
    friday: { available: true },
    saturday: { available: true },
    sunday: { available: false },
  };

  const DAY_LABELS = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  const [staffInfo, setStaffInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    permission: 'Owner',
    pinCode: '',
    selectedLocationType: 'All',
    selectedLocations: [],
    currency: 'USD',
    payrollType: 'Hourly',
    salaryAmount: '0',
    taxType: '',
    taxAmount: '0',
    paymentMethod: '',
    paymentEmail: '',
    paypalName: '',
    stripeEmail: '',
    stripeAccountId: '',
    wiseEmail: '',
    wiseAccountName: '',
    wiseCurrency: 'USD',
    payoneerEmail: '',
    payoneerName: '',
    bankAccountType: 'domestic',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    revolutContact: '',
    revolutUsername: '',
    venmoUsername: '',
    venmoPhone: '',
    cashTag: '',
    cashAppContact: '',
    availability: DEFAULT_AVAILABILITY,
  });

  const originalData = useRef();

  // App Bridge configuration
  const fetchStaffData = async () => {
    try {
      const response = await axiosClient.get(`/staff/${id}`);
      if (response.data.success) {
        const staff = response.data.staff;
        setStaffInfo({
          firstName: staff.first_name,
          lastName: staff.last_name,
          email: staff.email,
          phone: staff.phone,
          permission: staff.position,
          pinCode: staff.pinCode,
          selectedLocationType: staff.locations?.includes('All') ? 'All' : 'Specific',
          selectedLocations: staff.locations?.split(', ').filter(loc => loc !== 'All') || [],
          currency: staff.currency,
          payrollType: staff.payroll_type,
          salaryAmount: staff.salary_amount,
          taxType: staff.tax_type || '',
          taxAmount: staff.tax_amount ?? '0',
          paymentMethod: staff.payment_method,
          paymentEmail: staff.payment_email,
          // PayPal fields
          paypalName: staff.paypal_name || '',
          // Stripe fields
          stripeEmail: staff.stripe_email || '',
          stripeAccountId: staff.stripe_account_id || '',
          // Wise fields
          wiseEmail: staff.wise_email || '',
          wiseAccountName: staff.wise_account_name || '',
          wiseCurrency: staff.wise_currency || 'USD',
          // Payoneer fields
          payoneerEmail: staff.payoneer_email || '',
          payoneerName: staff.payoneer_name || '',
          // Bank Transfer fields
          bankAccountType: staff.bank_account_type || 'domestic',
          bankName: staff.bank_name || '',
          accountHolderName: staff.account_holder_name || '',
          accountNumber: staff.account_number || '',
          routingNumber: staff.routing_number || '',
          swiftCode: staff.swift_code || '',
          iban: staff.iban || '',
          // Revolut fields
          revolutContact: staff.revolut_contact || '',
          revolutUsername: staff.revolut_username || '',
          // Venmo fields
          venmoUsername: staff.venmo_username || '',
          venmoPhone: staff.venmo_phone || '',
          // Square Cash fields
          cashTag: staff.cash_tag || '',
          cashAppContact: staff.cash_app_contact || '',
          // Availability — fall back to defaults if not yet set
          availability: (() => {
            try {
              const parsed = staff.days_availability
                ? (typeof staff.days_availability === 'string'
                  ? JSON.parse(staff.days_availability)
                  : staff.days_availability)
                : null;
              return parsed && Object.keys(parsed).length === 7 ? parsed : DEFAULT_AVAILABILITY;
            } catch {
              return DEFAULT_AVAILABILITY;
            }
          })(),
        });
        originalData.current = JSON.parse(JSON.stringify(staffData));
        setIsDirty(false);
      }
    } catch (error) {
      setToastContent(t('failed_to_fetch_staff_details'));
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch staff data
  useEffect(() => {
    fetchStaffData();
  }, [id]);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosClient.get('/locations');
        if (response.data.success) {
          setLocations(response.data.locations);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Handle form changes
  const handleChange = (field) => (value) => {
    setStaffInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const handleAvailabilityChange = (day, field, value) => {
    setStaffInfo(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { ...prev.availability[day], [field]: value },
      },
    }));
    setIsDirty(true);
  };

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Basic Contact Information Validation
    if (!staffInfo.firstName.trim()) {
      newErrors.firstName = t('first_name_required');
    } else if (staffInfo.firstName.length < 2) {
      newErrors.firstName = t('first_name_min_length');
    }

    if (!staffInfo.lastName.trim()) {
      newErrors.lastName = t('last_name_required');
    } else if (staffInfo.lastName.length < 2) {
      newErrors.lastName = t('last_name_min_length');
    }

    if (!staffInfo.email.trim()) {
      newErrors.email = t('email_required');
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(staffInfo.email)) {
      newErrors.email = t('email_invalid');
    }

    if (staffInfo.phone && !/^\+?[\d\s-]{10,}$/.test(staffInfo.phone)) {
      newErrors.phone = t('phone_invalid');
    }

    // PIN Code Validation
    if (!staffInfo.pinCode) {
      newErrors.pinCode = t('pin_code_required');
    } else if (!/^\d{4,6}$/.test(staffInfo.pinCode)) {
      newErrors.pinCode = t('pin_code_invalid');
    }

    // Location Validation
    if (staffInfo.selectedLocationType === 'Specific' &&
      (!staffInfo.selectedLocations || staffInfo.selectedLocations.length === 0)) {
      newErrors.locations = t('location_required');
    }

    // Tax Validation - only when a tax type has actually been chosen.
    if (staffInfo.taxType) {
      const taxValue = parseFloat(staffInfo.taxAmount);
      if (staffInfo.taxAmount === '' || isNaN(taxValue) || taxValue < 0) {
        newErrors.taxAmount = t('tax_amount_invalid');
      } else if (staffInfo.taxType === 'percentage' && taxValue > 100) {
        newErrors.taxAmount = t('tax_percentage_max');
      }
    }

    // Salary/Wages Validation
    if (!staffInfo.salaryAmount || staffInfo.salaryAmount === '0') {
      newErrors.salaryAmount = t('salary_amount_required');
    } else if (isNaN(parseFloat(staffInfo.salaryAmount))) {
      newErrors.salaryAmount = t('salary_amount_invalid');
    }

    // Payment Method Validation
    if (staffInfo.paymentMethod) {
      switch (staffInfo.paymentMethod) {
        case 'PayPal':
          if (!staffInfo.paymentEmail) {
            newErrors.paymentEmail = t('paypal_email_required');
          }
          if (!staffInfo.paypalName) {
            newErrors.paypalName = t('paypal_name_required');
          }
          break;

        case 'Stripe':
          if (!staffInfo.stripeEmail) {
            newErrors.stripeEmail = t('stripe_email_required');
          }
          if (!staffInfo.stripeAccountId) {
            newErrors.stripeAccountId = t('stripe_account_id_required');
          }
          break;

        case 'Wise':
          if (!staffInfo.wiseEmail) {
            newErrors.wiseEmail = t('wise_email_required');
          }
          if (!staffInfo.wiseAccountName) {
            newErrors.wiseAccountName = t('wise_account_name_required');
          }
          break;

        case 'Payoneer':
          if (!staffInfo.payoneerEmail) {
            newErrors.payoneerEmail = t('payoneer_email_required');
          }
          if (!staffInfo.payoneerName) {
            newErrors.payoneerName = t('payoneer_name_required');
          }
          break;

        case 'Direct Deposit':
        case 'Bank Transfer':
          if (!staffInfo.bankName) {
            newErrors.bankName = t('bank_name_required');
          }
          if (!staffInfo.accountHolderName) {
            newErrors.accountHolderName = t('account_holder_name_required');
          }
          if (!staffInfo.accountNumber) {
            newErrors.accountNumber = t('account_number_required');
          }
          if (staffInfo.bankAccountType === 'domestic') {
            if (!staffInfo.routingNumber) {
              newErrors.routingNumber = t('routing_number_required');
            }
          } else {
            if (!staffInfo.swiftCode) {
              newErrors.swiftCode = t('swift_code_required');
            }
            if (!staffInfo.iban) {
              newErrors.iban = t('iban_required');
            }
          }
          break;

        case 'Revolut':
          if (!staffInfo.revolutContact) {
            newErrors.revolutContact = t('revolut_contact_required');
          }
          if (!staffInfo.revolutUsername) {
            newErrors.revolutUsername = t('revolut_username_required');
          }
          break;

        case 'Venmo':
          if (!staffInfo.venmoUsername) {
            newErrors.venmoUsername = t('venmo_username_required');
          }
          if (!staffInfo.venmoPhone) {
            newErrors.venmoPhone = t('venmo_phone_required');
          }
          break;

        case 'Square':
          if (!staffInfo.cashTag) {
            newErrors.cashTag = t('cash_tag_required');
          }
          if (!staffInfo.cashAppContact) {
            newErrors.cashAppContact = t('cash_app_contact_required');
          }
          break;
      }
    }

    // Set the errors and return validation result
    setErrors(newErrors);

    // If there are any errors, show a toast message
    if (Object.keys(newErrors).length > 0) {
      setShowToast(true);
      setToastContent(t('please_fill_in_all_required_fields'));
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setShowToast(true);
      setToastContent(t('please_fix_validation_errors'));
      appBridge.saveBar.hide('create-shopify-save-bar');
      return;
    }
    try {
      const dataToSend = {
        ...staffInfo,
        // Convert locations array to comma-separated string
        selectedLocations: staffInfo.selectedLocationType === 'All'
          ? 'All'
          : staffInfo.selectedLocations.join(', ')
      };

      const paymentMethodFields = {
        PayPal: ['paypalName', 'paymentEmail'],
        Stripe: ['stripeEmail', 'stripeAccountId'],
        Wise: ['wiseEmail', 'wiseAccountName', 'wiseCurrency'],
        Payoneer: ['payoneerEmail', 'payoneerName'],
        'Bank Transfer': ['bankAccountType', 'bankName', 'accountHolderName', 'accountNumber',
          'routingNumber', 'swiftCode', 'iban'],
        'Direct Deposit': ['bankAccountType', 'bankName', 'accountHolderName', 'accountNumber',
          'routingNumber', 'swiftCode', 'iban'],
        Revolut: ['revolutContact', 'revolutUsername'],
        Venmo: ['venmoUsername', 'venmoPhone'],
        Square: ['cashTag', 'cashAppContact']
      };

      const relevantFields = paymentMethodFields[staffInfo.paymentMethod] || [];
      const cleanedData = {
        ...dataToSend,
        // Keep only the relevant payment fields
        ...Object.fromEntries(
          Object.entries(dataToSend).filter(([key]) =>
            relevantFields.includes(key) || !Object.values(paymentMethodFields).flat().includes(key)
          )
        )
      };

      const response = await axiosClient.post(`/staff_update/${id}`, cleanedData);
      if (response.data.success) {
        setToastContent(t('staff_updated_successfully'));
        setShowToast(true);
        originalData.current = staffInfo;
        setIsDirty(false);
        shopify.toast.show(t('staff_updated'))
        appBridge.saveBar.hide('create-shopify-save-bar');
        setTimeout(() => navigate('/StaffManagement'), 1500);
      } else {
        // Handle server validation errors
        if (response.data.errors) {
          setErrors(response.data.errors);
          // Show first error message in toast
          const firstError = Object.values(response.data.errors)[0];
          shopify.toast.show(firstError[0] || t('validation_failed'), { error: true });
        } else {
          shopify.toast.show(response.data.message || t('failed_to_update_staff'), { error: true });
        }
        appBridge.saveBar.hide('create-shopify-save-bar');
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        // Handle validation errors from server
        setErrors(error.response.data.errors);
        const firstError = Object.values(error.response.data.errors)[0];
        shopify.toast.show(firstError[0] || t('validation_failed'), { error: true });
      } else {
        setToastContent(t('error_updating_staff'));
        shopify.toast.show(t('error_updating_staff'), { error: true });
      }
      appBridge.saveBar.hide('create-shopify-save-bar');
    }
  };
  // Add this useEffect to ensure isDirty is set correctly when form values change
  useEffect(() => {
    // Only check for changes if originalData has been set
    if (originalData.current) {
      const hasChanges = JSON.stringify(staffInfo) !== JSON.stringify(originalData.current);
      setIsDirty(hasChanges);
    }
  }, [staffInfo]);
  // Handle discard
  const handleDiscard = () => {
    // Reset form to original data
    if (originalData.current) {
      setStaffInfo(JSON.parse(JSON.stringify(originalData.current)));
    }
    setErrors({});
    setIsDirty(false);
    appBridge.saveBar.hide('create-shopify-save-bar');
  };

  // Setup contextual save bar

  if (isLoading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spinner accessibilityLabel={t('loading')} size="large" />
        </div>
      </Page>
    );
  }

  const handleBackClick = () => {
    navigate('/StaffManagement');
  };

  return (
    <Page
      title={t('edit_staff')}
      fullWidth
      backAction={{
        content: t('staff_management'),
        onAction: handleBackClick,
      }}
    >
      {Object.keys(errors).length > 0 && (
        <Banner
          title={t('please_correct_following_errors')}
          tone="critical"
        >
          <List type="bullet">
            {Object.entries(errors).map(([field, messages]) => (
              <List.Item key={field}>
                {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${Array.isArray(messages) ? messages[0] : messages}`}
              </List.Item>
            ))}
          </List>
        </Banner>
      )}
      <Form onSubmit={handleSave}>
        <Layout>
          {/* Contact Information */}
          <Layout.AnnotatedSection
            title={t('basic_information')}
            description={t('manage_contact_information_and_permissions')}
          >
            <Card sectioned>
              <div className="flex gap-6">
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('first_name')}
                    value={staffInfo.firstName}
                    onChange={handleChange('firstName')}
                    placeholder={t('enter_first_name')}
                    error={errors.firstName && Array.isArray(errors.firstName) ? errors.firstName[0] : errors.firstName}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('last_name')}
                    value={staffInfo.lastName}
                    onChange={handleChange('lastName')}
                    placeholder={t('enter_last_name')}
                    error={errors.lastName && Array.isArray(errors.lastName) ? errors.lastName[0] : errors.lastName}
                  />
                </div>
              </div>
              <div className="flex gap-6 mt-4">
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('email')}
                    type="email"
                    value={staffInfo.email}
                    onChange={handleChange('email')}
                    placeholder={t('enter_email')}
                    error={errors.email && Array.isArray(errors.email) ? errors.email[0] : errors.email}
                  />
                </div>
                <div className="w-full md:w-1/2">
                  <TextField
                    label={t('phone_optional')}
                    type="tel"
                    value={staffInfo.phone}
                    onChange={handleChange('phone')}
                    placeholder={t('enter_phone_number')}
                  />
                </div>
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Position */}
          <Layout.AnnotatedSection
            title={t('permissions')}
            description={t('assign_appropriate_access_level')}
          >
            <Card sectioned>
              <Select
                label={t('position')}
                options={[
                  { label: t('owner'), value: 'Owner' },
                  { label: t('staff'), value: 'Staff' },
                  { label: t('manager'), value: 'Manager' },
                ]}
                value={staffInfo.permission}
                onChange={handleChange('permission')}
                placeholder={t('select_access_level')}
                error={errors.permission}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* POS PIN Code */}
          <Layout.AnnotatedSection
            title={t('pin_code')}
            description={t('set_4_digit_pin_for_pos')}
          >
            <Card sectioned>
              <div className="space-y-2">
                <p className="text-gray-700">
                  {t('staff_need_unique_pin')}
                </p>
                <div className="flex gap-4">
                  <div className="w-28">
                    <TextField
                      label={t('pin')}
                      type="number"
                      value={staffInfo.pinCode}
                      onChange={handleChange('pinCode')}
                      placeholder=""
                      error={errors.pinCode && Array.isArray(errors.pinCode) ? errors.pinCode[0] : errors.pinCode}
                    />
                  </div>
                  <div style={{ marginTop: "25px", marginLeft: "10px" }}>
                    <Button
                      onClick={() =>
                        setStaffInfo((prev) => ({
                          ...prev,
                          pinCode: Math.floor(1000 + Math.random() * 9000).toString(),
                        }))
                      }
                      size="medium"
                    >
                      {t('generate_random')}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Locations */}
          <Layout.AnnotatedSection
            title={t('location_access')}
            description={t('select_locations_for_staff_access')}
          >
            <Card sectioned>
              <div className="flex items-center gap-6">
                <ChoiceList
                  title=""
                  choices={[
                    { label: t('all_locations'), value: 'All' },
                    { label: t('specific_locations'), value: 'Specific' },
                  ]}
                  selected={[staffInfo.selectedLocationType]}
                  onChange={(value) =>
                    setStaffInfo((prev) => ({
                      ...prev,
                      selectedLocationType: value[0],
                      // Reset selected locations when switching types
                      selectedLocations: value[0] === 'All' ? [] : prev.selectedLocations
                    }))
                  }
                />
              </div>

              {staffInfo.selectedLocationType === 'Specific' && (
                <div className="ml-6 mt-4">
                  <p className="font-semibold">{t('available_locations')}</p>
                  {isLoadingLocations ? (
                    <div>{t('loading_locations')}</div>
                  ) : locations.length === 0 ? (
                    <div>{t('no_locations_available')}</div>
                  ) : (
                    <div className="space-y-4">
                      {locations.map((location) => (
                        <Checkbox
                          key={location.id}
                          label={
                            <>
                              <strong>{location.name}</strong>
                              <br />
                              <span className="text-sm text-gray-500">
                                {location.address}
                              </span>
                            </>
                          }
                          checked={staffInfo.selectedLocations?.includes(location.name)} // Changed from location.id to location.name
                          onChange={(checked) => {
                            setStaffInfo((prev) => ({
                              ...prev,
                              selectedLocations: checked
                                ? [...(prev.selectedLocations || []), location.name] // Changed from location.id to location.name
                                : (prev.selectedLocations || []).filter(name => name !== location.name) // Changed from id to name
                            }));
                            setIsDirty(true);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </Layout.AnnotatedSection>


          {/* Salary/Wages */}
          <Layout.AnnotatedSection
            title={t('payroll_information')}
            description={t('configure_payment_methods_and_salary_details')}
          >
            <Card sectioned>
              <div className="flex gap-6">
                <div className="w-full md:w-1/3">
                  <Select
                    label={t('currency')}
                    options={CURRENCIES}
                    value={staffInfo.currency}
                    onChange={handleChange('currency')}
                    helpText={t('select_currency_for_payments')}
                    error={errors.currency}
                  />
                </div>
                <div className="w-full md:w-1/3">
                  <Select
                    label={t('payroll_type')}
                    options={[
                      { label: t('hourly'), value: 'Hourly' },
                      { label: t('weekly'), value: 'Weekly' },
                      { label: t('monthly'), value: 'Monthly' },
                    ]}
                    value={staffInfo.payrollType}
                    onChange={handleChange('payrollType')}
                    error={errors.payrollType}
                  />
                </div>
                <div className="w-full md:w-1/3">
                  <TextField
                    label={t('salary_amount')}
                    type="text"
                    value={staffInfo.salaryAmount}
                    onChange={handleChange('salaryAmount')}
                    placeholder=""
                    error={errors.salaryAmount}
                  />
                </div>
                <div className="w-full md:w-1/3">
                  <Select
                    label={t('tax_type')}
                    options={[
                      { label: t('tax_none'), value: '' },
                      { label: t('tax_percentage'), value: 'percentage' },
                      { label: t('tax_fixed'), value: 'fixed' },
                    ]}
                    value={staffInfo.taxType}
                    onChange={handleChange('taxType')}
                    error={errors.taxType}
                  />
                </div>
                {staffInfo.taxType ? (
                  <div className="w-full md:w-1/3">
                    <TextField
                      label={staffInfo.taxType === 'percentage' ? t('tax_rate') : t('tax_amount')}
                      type="text"
                      value={staffInfo.taxAmount}
                      onChange={handleChange('taxAmount')}
                      placeholder={staffInfo.taxType === 'percentage' ? '15' : '50'}
                      suffix={staffInfo.taxType === 'percentage' ? '%' : undefined}
                      helpText={t('tax_help')}
                      error={errors.taxAmount}
                    />
                  </div>
                ) : null}
              </div>
            </Card>
          </Layout.AnnotatedSection>

          {/* Availability */}
          <Layout.AnnotatedSection
            title="Weekly Availability"
            description="Set the days this staff member is available to work. Managers will see a warning if they schedule outside these days."
          >
            <Card sectioned>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {DAY_LABELS.map(({ key, label }) => {
                  const isAvailable = staffInfo.availability[key].available;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleAvailabilityChange(key, 'available', !isAvailable)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: `2px solid ${isAvailable ? '#008060' : '#C9CCCF'}`,
                        backgroundColor: isAvailable ? '#008060' : '#F6F6F7',
                        color: isAvailable ? '#FFFFFF' : '#6D7175',
                        fontWeight: isAvailable ? '600' : '400',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        userSelect: 'none',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p style={{ marginTop: '10px', fontSize: '12px', color: '#6D7175' }}>
                Click a day to toggle availability. Green = available.
              </p>
            </Card>
          </Layout.AnnotatedSection>

          {/* Payment Method */}
          <Layout.AnnotatedSection
            title={t('payment_method')}
            description={t('choose_preferred_payment_method')}
          >
            <Card sectioned>
              <div className="space-y-4">
                <Select
                  label={t('payment_method')}
                  options={[
                    { label: t('paypal'), value: 'PayPal' },
                    { label: t('stripe'), value: 'Stripe' },
                    { label: t('wise'), value: 'Wise' },
                    { label: t('payoneer'), value: 'Payoneer' },
                    { label: t('bank_transfer'), value: 'Bank Transfer' },
                    { label: t('direct_deposit'), value: 'Direct Deposit' },
                    { label: 'Cash', value: 'Cash' },
                    { label: 'Check', value: 'Check' },
                    { label: t('revolut'), value: 'Revolut' },
                    { label: t('venmo'), value: 'Venmo' },
                    { label: t('square'), value: 'Square' }
                  ]}
                  value={staffInfo.paymentMethod}
                  onChange={handleChange('paymentMethod')}
                  placeholder={t('select_payment_method')}
                  error={errors.paymentMethod}
                />

                {/* PayPal Fields */}
                {staffInfo.paymentMethod === 'PayPal' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('paypal_email')}
                      type="email"
                      value={staffInfo.paymentEmail}
                      onChange={handleChange('paymentEmail')}
                      placeholder={t('enter_paypal_email_address')}
                      helpText={t('enter_paypal_email_where_payments_sent')}
                      error={errors.paymentEmail}
                    />
                    <TextField
                      label={t('paypal_account_name')}
                      value={staffInfo.paypalName || ''}
                      onChange={handleChange('paypalName')}
                      placeholder={t('account_holder_name_in_paypal')}
                      error={errors.paypalName}
                    />
                  </div>
                )}

                {/* Stripe Fields */}
                {staffInfo.paymentMethod === 'Stripe' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('stripe_account_email')}
                      type="email"
                      value={staffInfo.stripeEmail || ''}
                      onChange={handleChange('stripeEmail')}
                      placeholder={t('stripe_account_email_address')}
                      error={errors.stripeEmail}
                    />
                    <TextField
                      label={t('stripe_account_id')}
                      value={staffInfo.stripeAccountId || ''}
                      onChange={handleChange('stripeAccountId')}
                      placeholder="acct_xxxxxxxxxxxxx"
                      helpText={t('stripe_connect_account_id')}
                      error={errors.stripeAccountId}
                    />
                  </div>
                )}

                {/* Wise Fields */}
                {staffInfo.paymentMethod === 'Wise' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('wise_email')}
                      type="email"
                      value={staffInfo.wiseEmail || ''}
                      onChange={handleChange('wiseEmail')}
                      placeholder={t('wise_account_email')}
                      error={errors.wiseEmail}
                    />
                    <TextField
                      label={t('wise_account_holder_name')}
                      value={staffInfo.wiseAccountName || ''}
                      onChange={handleChange('wiseAccountName')}
                      placeholder={t('name_on_wise_account')}
                      error={errors.wiseAccountName}
                    />
                    <Select
                      label={t('wise_account_currency')}
                      options={[
                        { label: 'USD', value: 'USD' },
                        { label: 'EUR', value: 'EUR' },
                        { label: 'GBP', value: 'GBP' },
                        { label: 'AUD', value: 'AUD' },
                        { label: 'CAD', value: 'CAD' }
                      ]}
                      value={staffInfo.wiseCurrency || 'USD'}
                      onChange={handleChange('wiseCurrency')}
                      error={errors.wiseCurrency}
                    />
                  </div>
                )}

                {/* Payoneer Fields */}
                {staffInfo.paymentMethod === 'Payoneer' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('payoneer_email')}
                      type="email"
                      value={staffInfo.payoneerEmail || ''}
                      onChange={handleChange('payoneerEmail')}
                      placeholder={t('payoneer_account_email')}
                      error={errors.payoneerEmail}
                    />
                    <TextField
                      label={t('payoneer_account_name')}
                      value={staffInfo.payoneerName || ''}
                      onChange={handleChange('payoneerName')}
                      placeholder={t('name_on_payoneer_account')}
                      error={errors.payoneerName}
                    />
                  </div>
                )}

                {/* Bank Transfer / Direct Deposit Fields */}
                {(staffInfo.paymentMethod === 'Bank Transfer' || staffInfo.paymentMethod === 'Direct Deposit') && (
                  <div className="space-y-4">
                    <Select
                      label={t('bank_account_type')}
                      options={[
                        { label: t('domestic'), value: 'domestic' },
                        { label: t('international_swift'), value: 'international' }
                      ]}
                      value={staffInfo.bankAccountType || 'domestic'}
                      onChange={handleChange('bankAccountType')}
                      error={errors.bankAccountType}
                    />
                    <TextField
                      label={t('bank_name')}
                      value={staffInfo.bankName || ''}
                      onChange={handleChange('bankName')}
                      placeholder={t('enter_bank_name')}
                      error={errors.bankName}
                    />
                    <TextField
                      label={t('account_holder_name')}
                      value={staffInfo.accountHolderName || ''}
                      onChange={handleChange('accountHolderName')}
                      placeholder={t('enter_account_holder_name')}
                      error={errors.accountHolderName}
                    />
                    <TextField
                      label={t('account_number')}
                      value={staffInfo.accountNumber || ''}
                      onChange={handleChange('accountNumber')}
                      placeholder={t('enter_account_number')}
                      error={errors.accountNumber}
                    />
                    {staffInfo.bankAccountType === 'domestic' ? (
                      <TextField
                        label={t('routing_number')}
                        value={staffInfo.routingNumber || ''}
                        onChange={handleChange('routingNumber')}
                        placeholder={t('enter_routing_number')}
                        error={errors.routingNumber}
                      />
                    ) : (
                      <>
                        <TextField
                          label={t('swift_bic_code')}
                          value={staffInfo.swiftCode || ''}
                          onChange={handleChange('swiftCode')}
                          placeholder={t('enter_swift_bic_code')}
                          error={errors.swiftCode}
                        />
                        <TextField
                          label={t('iban')}
                          value={staffInfo.iban || ''}
                          onChange={handleChange('iban')}
                          placeholder={t('enter_iban')}
                          error={errors.iban}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Revolut Fields */}
                {staffInfo.paymentMethod === 'Revolut' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('revolut_email_phone')}
                      value={staffInfo.revolutContact || ''}
                      onChange={handleChange('revolutContact')}
                      placeholder={t('email_or_phone_linked_to_revolut')}
                      error={errors.revolutContact}
                    />
                    <TextField
                      label={t('revolut_username')}
                      value={staffInfo.revolutUsername || ''}
                      onChange={handleChange('revolutUsername')}
                      placeholder={t('username_if_applicable')}
                      error={errors.revolutUsername}
                    />
                  </div>
                )}

                {/* Venmo Fields */}
                {staffInfo.paymentMethod === 'Venmo' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('venmo_username')}
                      value={staffInfo.venmoUsername || ''}
                      onChange={handleChange('venmoUsername')}
                      placeholder="@username"
                      error={errors.venmoUsername}
                    />
                    <TextField
                      label={t('venmo_phone_number')}
                      value={staffInfo.venmoPhone || ''}
                      onChange={handleChange('venmoPhone')}
                      placeholder={t('phone_number_linked_to_venmo')}
                      error={errors.venmoPhone}
                    />
                  </div>
                )}

                {/* Square Cash Fields */}
                {staffInfo.paymentMethod === 'Square' && (
                  <div className="space-y-4">
                    <TextField
                      label={t('cash_app_cashtag')}
                      value={staffInfo.cashTag || ''}
                      onChange={handleChange('cashTag')}
                      placeholder="$cashtag"
                      error={errors.cashTag}
                    />
                    <TextField
                      label={t('cash_app_phone_email')}
                      value={staffInfo.cashAppContact || ''}
                      onChange={handleChange('cashAppContact')}
                      placeholder={t('phone_or_email_linked_to_cash_app')}
                      error={errors.cashAppContact}
                    />
                  </div>
                )}
              </div>
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>
      <div style={{ marginTop: "20px" }}></div>

      {isDirty && (
        <SaveBar
          id="create-shopify-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
                setIsDirty(false);
              } catch (err) {
                // Error handled silently
              }
            }}
          >
            {t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}
    </Page>
  );
}
